import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { CardService } from '../services/cardService';
import { RewardService } from '../services/rewardService';
import { StatsService } from '../services/statsService';
import { TripService } from '../services/tripService';
import { UserCardService } from '../services/userCardService';
import { ExpenseRequestService } from '../services/expenseRequestService';
import type { ApiResponse } from '../types/card';
import type { RewardApiResponse, Reward } from '../types/reward';
import { useAuth } from '../context/AuthContext';
import DateRangePicker from './DateRangePicker';

interface DashboardStats {
  totalCards: number;
  activeCards: number;
  inactiveCards: number;
  totalBanks: number;
  recentlyAdded: number;
  totalRewards: number;
  activeRewards: number;
  totalUsers: number;
  totalCommunityPosts: number;
  totalComments: number;
  totalExpenses: number;
  totalExpenseRequests: number;
  totalTrips: number;
  totalTripRequests: number;
  totalUserCards: number;
}

const IST_OFFSET_MS = 330 * 60 * 1000;

function toIstStartOfDayUtc(date: Date): Date {
  const year = date.getFullYear();
  const monthIndex = date.getMonth();
  const dayOfMonth = date.getDate();
  return new Date(Date.UTC(year, monthIndex, dayOfMonth, 0, 0, 0, 0) - IST_OFFSET_MS);
}

function toIstEndOfDayUtc(date: Date): Date {
  const year = date.getFullYear();
  const monthIndex = date.getMonth();
  const dayOfMonth = date.getDate();
  // End of day in IST (23:59:59.999 IST)
  return new Date(Date.UTC(year, monthIndex, dayOfMonth, 23, 59, 59, 999) - IST_OFFSET_MS);
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [stats, setStats] = useState<DashboardStats>({
    totalCards: 0,
    activeCards: 0,
    inactiveCards: 0,
    totalBanks: 0,
    recentlyAdded: 0,
    totalRewards: 0,
    activeRewards: 0,
    totalUsers: 0,
    totalCommunityPosts: 0,
    totalComments: 0,
    totalExpenses: 0,
    totalExpenseRequests: 0,
    totalTrips: 0,
    totalTripRequests: 0,
    totalUserCards: 0,
  });
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [rangeHydrated, setRangeHydrated] = useState(false);
  const [preset, setPreset] = useState<
    'all' | 'today' | 'yesterday' | 'thisWeek' | 'lastWeek' | 'last7' | 'last30' | 'thisMonth' | 'lastMonth' | 'custom'
  >('all');

  // Load persisted range on first mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem('dashboardDateRange');
      if (raw) {
        const parsed = JSON.parse(raw) as { startDate?: string; endDate?: string; preset?: string };
        if (parsed.startDate) setStartDate(new Date(parsed.startDate));
        if (parsed.endDate) setEndDate(new Date(parsed.endDate));
        if (parsed.preset && ['all','today','yesterday','thisWeek','lastWeek','last7','last30','thisMonth','lastMonth','custom'].includes(parsed.preset)) {
          setPreset(parsed.preset as any);
        } else if (parsed.startDate || parsed.endDate) {
          setPreset('custom');
        }
      }
    } catch {
      // ignore
    } finally {
      setRangeHydrated(true);
    }
  }, []);

  const hasActiveFilter = useMemo(() => preset !== 'all' && !!(startDate || endDate), [preset, startDate, endDate]);

  const countExpenseRequestsInRange = useCallback(
    async (rangeStart: Date | null, rangeEnd: Date | null): Promise<number> => {
      // The Expense Requests list endpoint does not support date filtering.
      // For dashboard date-range filtering, count by paging through results (sorted newest-first)
      // and stop once we reach items older than the rangeStart.
      const limit = 200;
      let page = 1;
      let total = 0;

      const startMs = rangeStart ? toIstStartOfDayUtc(rangeStart).getTime() : null;
      const endMs = rangeEnd ? toIstEndOfDayUtc(rangeEnd).getTime() : null;

      // If neither boundary exists, caller should use the server-provided total instead.
      if (startMs == null && endMs == null) return 0;

      while (true) {
        const res = await ExpenseRequestService.listExpenseRequests({ page, limit, sort: '-createdAt' });
        const items = res.items || [];
        if (!items.length) break;

        let shouldStop = false;
        for (const item of items) {
          const createdAtRaw = (item as any)?.createdAt;
          const createdAt = new Date(createdAtRaw);
          const createdMs = createdAt.getTime();
          if (!Number.isFinite(createdMs)) continue;

          if (endMs != null && createdMs > endMs) {
            // Too new; keep scanning.
            continue;
          }

          if (startMs != null && createdMs < startMs) {
            // Too old; since results are sorted DESC, we can stop completely.
            shouldStop = true;
            break;
          }

          total += 1;
        }

        if (shouldStop) break;
        if (page >= (res.pages || 1)) break;
        page += 1;
      }

      return total;
    },
    []
  );

  const fetchDashboardStats = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch stats from Stats API
      let apiStats: any = undefined;
      if (user?.role === 'admin') {
        try {
          const params: { startDate?: string; endDate?: string; models: string[] } = {
            models: ['cards', 'users', 'community', 'comments', 'expenses', 'trips', 'rewardsv1', 'tripRequest', 'user_cards']
          };
          const startStr = startDate ? toIstStartOfDayUtc(startDate).toISOString() : '';
          const endStr = endDate ? toIstEndOfDayUtc(endDate).toISOString() : '';
          if (startStr) params.startDate = startStr;
          if (endStr) params.endDate = endStr;
          apiStats = await StatsService.getCounts(params);
        } catch (error) {
          console.error('Error fetching stats API:', error);
        }
      }

      // Fetch cards stats
      const cardsResponse: ApiResponse = await CardService.getCards({ page: 1, limit: 1000 });
      
      // Fetch rewards stats
      let rewardsResponse: RewardApiResponse;
      try {
        rewardsResponse = await RewardService.getRewards({ page: 1, limit: 1000 });
      } catch {
        // If rewards API fails, use default values
        rewardsResponse = { data: [], total: 0, pages: 1, page: 1, count: 0, success: false };
      }

      // Fetch trip requests stats
      let tripRequestsTotal = 0;
      try {
        const tripRequestsResponse = await TripService.listTripRequests({ page: 1, limit: 1 });
        tripRequestsTotal = tripRequestsResponse.total ?? 0;
      } catch (error) {
        console.error('Error fetching trip requests:', error);
      }

      // Fetch user cards stats
      let userCardsTotal = 0;
      try {
        const userCardsResponse = await UserCardService.listAdminUserCards({ page: 1, limit: 1 });
        userCardsTotal = userCardsResponse.total ?? 0;
      } catch (error) {
        console.error('Error fetching user cards:', error);
      }

      // Fetch expense requests stats
      let expenseRequestsTotal = 0;
      try {
        if (user?.role === 'admin') {
          if (startDate || endDate) {
            expenseRequestsTotal = await countExpenseRequestsInRange(startDate, endDate);
          } else {
            const expenseRequestsResponse = await ExpenseRequestService.listExpenseRequests({ page: 1, limit: 1 });
            expenseRequestsTotal = expenseRequestsResponse.total ?? 0;
          }
        }
      } catch (error) {
        console.error('Error fetching expense requests:', error);
      }
      
      if (cardsResponse.data) {
        const cards = cardsResponse.data;
        const activeCards = cards.filter(card => card.active).length;
        const uniqueBanks = new Set(cards.map(card => card.bankName)).size;
        
        const rewards: Reward[] = rewardsResponse.data || [];
        const activeRewards = rewards.filter((reward: Reward) => reward.status === 'active').length;
        
        setStats({
          totalCards: apiStats?.counts?.cards?.count ?? cardsResponse.total ?? cards.length,
          activeCards,
          inactiveCards: cards.length - activeCards,
          totalBanks: uniqueBanks,
          recentlyAdded: Math.floor(Math.random() * 5) + 1, // Mock data
          totalRewards: apiStats?.counts?.rewardsv1?.count ?? rewardsResponse.total ?? rewards.length,
          activeRewards,
          totalUsers: apiStats?.counts?.users?.count ?? 0,
          totalCommunityPosts: apiStats?.counts?.community?.count ?? 0,
          totalComments: apiStats?.counts?.comments?.count ?? 0,
          totalExpenses: apiStats?.counts?.expenses?.count ?? 0,
          totalExpenseRequests: (apiStats as any)?.counts?.expenseRequest?.count ?? (apiStats as any)?.counts?.expense_request?.count ?? expenseRequestsTotal,
          totalTrips: apiStats?.counts?.trips?.count ?? 0,
          totalTripRequests: apiStats?.counts?.tripRequest?.count ?? tripRequestsTotal,
          totalUserCards: apiStats?.counts?.user_cards?.count ?? userCardsTotal,
        });
      }
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.role, startDate?.getTime(), endDate?.getTime(), countExpenseRequestsInRange]);

  useEffect(() => {
    // Avoid fetching with default (all-time) dates before we restore the persisted range.
    // Also wait for auth to resolve so admin-only stats are requested correctly.
    if (!rangeHydrated) return;
    if (authLoading) return;
    fetchDashboardStats();
  }, [fetchDashboardStats, rangeHydrated, authLoading]);

  // Helpers to compute preset ranges
  const applyPreset = (p: typeof preset) => {
    const now = new Date();
    const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0);
    const endOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
    const startOfWeek = (d: Date) => {
      const day = d.getDay(); // 0 Sun..6 Sat
      const diff = (day === 0 ? -6 : 1) - day; // make Monday start
      const s = new Date(d);
      s.setDate(d.getDate() + diff);
      return startOfDay(s);
    };
    const endOfWeek = (d: Date) => {
      const s = startOfWeek(d);
      const e = new Date(s);
      e.setDate(s.getDate() + 6);
      return endOfDay(e);
    };
    const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0);
    const endOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);

    setPreset(p);
    if (p === 'all') {
      setStartDate(null);
      setEndDate(null);
      localStorage.removeItem('dashboardDateRange');
      return;
    }
    let s: Date | null = null;
    let e: Date | null = null;
    switch (p) {
      case 'today':
        s = startOfDay(now);
        e = now;
        break;
      case 'yesterday': {
        const y = new Date(now);
        y.setDate(now.getDate() - 1);
        s = startOfDay(y);
        e = endOfDay(y);
        break;
      }
      case 'thisWeek':
        s = startOfWeek(now);
        e = endOfWeek(now);
        break;
      case 'lastWeek': {
        const lwEnd = new Date(startOfWeek(now));
        lwEnd.setDate(lwEnd.getDate() - 1);
        const lwStart = new Date(lwEnd);
        lwStart.setDate(lwEnd.getDate() - 6);
        s = startOfDay(lwStart);
        e = endOfDay(lwEnd);
        break;
      }
      case 'last7':
        s = new Date(now);
        s.setDate(now.getDate() - 6);
        s = startOfDay(s);
        e = now;
        break;
      case 'last30':
        s = new Date(now);
        s.setDate(now.getDate() - 29);
        s = startOfDay(s);
        e = now;
        break;
      case 'thisMonth':
        s = startOfMonth(now);
        e = endOfMonth(now);
        break;
      case 'lastMonth': {
        const lm = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        s = startOfMonth(lm);
        e = endOfMonth(lm);
        break;
      }
      case 'custom':
      default:
        // do nothing; user will set fields
        break;
    }
    if (s && e) {
      setStartDate(s);
      setEndDate(e);
      const sStr = s.toISOString();
      const eStr = e.toISOString();
      localStorage.setItem('dashboardDateRange', JSON.stringify({ startDate: sStr, endDate: eStr, preset: p }));
    }
  };

  const handleDateRangeChange = (start: Date | null, end: Date | null) => {
    // Only update if dates actually changed
    const startChanged = start?.getTime() !== startDate?.getTime();
    const endChanged = end?.getTime() !== endDate?.getTime();
    
    if (startChanged || endChanged) {
      setStartDate(start);
      setEndDate(end);
      setPreset(start || end ? 'custom' : 'all');
      if (start || end) {
        const sStr = start ? start.toISOString() : '';
        const eStr = end ? end.toISOString() : '';
        localStorage.setItem('dashboardDateRange', JSON.stringify({ startDate: sStr, endDate: eStr, preset: 'custom' }));
      } else {
        localStorage.removeItem('dashboardDateRange');
      }
    }
  };

  const handlePresetSelect = (presetName: string) => {
    // Preset is already applied by DateRangePicker, just update our preset state
    if (presetName === 'today') setPreset('today');
    else if (presetName === 'yesterday') setPreset('yesterday');
    else if (presetName === 'lastWeek') setPreset('lastWeek');
    else if (presetName === 'lastMonth') setPreset('lastMonth');
    else if (presetName === 'lastQuarter') setPreset('custom');
    else setPreset('custom');
  };

  const resetRange = () => {
    setPreset('all');
    setStartDate(null);
    setEndDate(null);
    localStorage.removeItem('dashboardDateRange');
  };

  const StatCard: React.FC<{
    title: string;
    value: string | number;
    icon: React.ReactNode;
    color: string;
    to?: string;
  }> = ({ title, value, icon, color, to }) => {
    const card = (
      <div
        className={`bg-white overflow-hidden shadow-sm rounded-lg border border-gray-200 transition-shadow ${
          to ? 'hover:shadow-md' : ''
        }`}
      >
        <div className="p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className={`w-10 h-10 ${color} rounded-lg flex items-center justify-center`}>
                {icon}
              </div>
            </div>
            <div className="ml-3 w-0 flex-1">
              <dl>
                <dt className="text-xs font-medium text-gray-500 truncate">{title}</dt>
                <dd className="flex items-baseline">
                  <div className="text-xl font-semibold text-gray-900">
                    {loading ? <div className="animate-pulse h-6 w-12 bg-gray-200 rounded" /> : value}
                  </div>
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>
    );

    if (!to) return card;

    return (
      <button
        type="button"
        onClick={() => navigate(to)}
        className="text-left w-full focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-lg"
        aria-label={`Open ${title}`}
      >
        {card}
      </button>
    );
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="mt-1 text-sm text-gray-500">
            Welcome back! Here's what's happening with your rewards platform.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <div className="w-full sm:w-80">
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              onChange={handleDateRangeChange}
              onPresetSelect={handlePresetSelect}
              onReset={resetRange}
            />
          </div>
          {user?.role !== 'admin' && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2 whitespace-nowrap">
              Note: Detailed stats are visible to admin users only.
            </div>
          )}
        </div>
      </div>

      {/* Stats grid - Organized by type */}
      <div className="space-y-8">
        {/* Cards & Rewards Section */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Cards & Rewards</h2>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              title="Total Credit Cards"
              value={stats.totalCards}
              to="/cards"
              color="bg-gradient-to-r from-blue-500 to-blue-600"
              icon={
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
              }
            />

            <StatCard
              title="Active Cards"
              value={stats.activeCards}
              to="/cards"
              color="bg-gradient-to-r from-green-500 to-green-600"
              icon={
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              }
            />

            <StatCard
              title="Inactive Cards"
              value={stats.inactiveCards}
              to="/cards"
              color="bg-gradient-to-r from-orange-500 to-orange-600"
              icon={
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              }
            />

            <StatCard
              title="Partner Banks"
              value={stats.totalBanks}
              to="/banks"
              color="bg-gradient-to-r from-purple-500 to-purple-600"
              icon={
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              }
            />

            <StatCard
              title="Total Rewards"
              value={stats.totalRewards}
              to="/rewards"
              color="bg-gradient-to-r from-yellow-500 to-yellow-600"
              icon={
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              }
            />

            <StatCard
              title="Active Rewards"
              value={stats.activeRewards}
              to="/rewards"
              color="bg-gradient-to-r from-emerald-500 to-emerald-600"
              icon={
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              }
            />

            <StatCard
              title="User Cards"
              value={stats.totalUserCards}
              to="/user-cards"
              color="bg-gradient-to-r from-amber-500 to-amber-600"
              icon={
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              }
            />

            <StatCard
              title="Recently Added"
              value={stats.recentlyAdded}
              color="bg-gradient-to-r from-indigo-500 to-indigo-600"
              icon={
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              }
            />
          </div>
        </div>

        {/* Users & Community Section */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Users & Community</h2>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              title="Total Users"
              value={stats.totalUsers}
              to="/users"
              color="bg-gradient-to-r from-pink-500 to-pink-600"
              icon={
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                </svg>
              }
            />

            <StatCard
              title="Community Posts"
              value={stats.totalCommunityPosts}
              to="/community"
              color="bg-gradient-to-r from-cyan-500 to-cyan-600"
              icon={
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a2 2 0 01-2-2v-6a2 2 0 012-2h8z" />
                </svg>
              }
            />

            <StatCard
              title="Total Comments"
              value={stats.totalComments}
              to="/comments"
              color="bg-gradient-to-r from-lime-500 to-lime-600"
              icon={
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              }
            />
          </div>
        </div>

        {/* Trips & Travel Section */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Trips & Travel</h2>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              title="Total Trips"
              value={stats.totalTrips}
              to="/trips"
              color="bg-gradient-to-r from-violet-500 to-violet-600"
              icon={
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              }
            />

            <StatCard
              title="Trip Requests"
              value={stats.totalTripRequests}
              to="/trips/requests"
              color="bg-gradient-to-r from-rose-500 to-rose-600"
              icon={
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
              }
            />
          </div>
        </div>

        {/* Expenses Section */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Expenses</h2>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              title="Total Expenses"
              value={stats.totalExpenses}
              to="/expenses"
              color="bg-gradient-to-r from-indigo-500 to-indigo-600"
              icon={
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              }
            />

            <StatCard
              title="Expense Requests"
              value={stats.totalExpenseRequests}
              to="/expenses/requests"
              color="bg-gradient-to-r from-fuchsia-500 to-fuchsia-600"
              icon={
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m-6-8h6M7 20h10a2 2 0 002-2V6a2 2 0 00-2-2H9l-2 2H7a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
